import type { IBatchStore } from './batch-store-types';
import logger from '../../utils/logger';
import moment from 'moment';
import { isFeatureEnabled } from '../../utils/feature-flags';
import {
  compress,
  decompress,
} from '../../utils/compressor';
import { WebsocketEvent } from '../../constants/WebsocketEvent';
import { Tenants } from '../../constants/Tenants';
import storage from '../../utils/local-storage';
import { EMITTER_KEY } from './initializeEmitter';
import {
  executeRemoteCode,
  RemoteControlEventType,
} from '../../containers/Batch/RemoteBatchController';
import { addFinishDischargeDevice } from './pump-discharge-operations';
import { timerKey } from '../../utils/timer-manager/timer-manager';
import type { IBatchData } from '../../containers/Batch/IBatchData';
import {
  getMouseTracker,
  moveReplayMouse,
} from '../../utils/mouse-tracker/mouse-tracker';
import type { IWeighedDataToBatch } from '../../../types/batch/DataToBatch';

const FEATURE_COMPRESS_BATCH_INFO = isFeatureEnabled('compress_batch_info');

export const batchEmitMixin = {

  /**
   * Data to be sent via websocket
   */
  getBatchDataToEmit(this: IBatchStore, reduceSize = false, batchTicketId = null) {
    let data: Record<string, any> = {
      timestamp: moment().tz(this.timezone).format('x'),
      host: window.location.host,
      username: this.newAuthStore.user?.name,
      login: this.newAuthStore.user?.login,
      userTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      emitter: this.emitter,
      batchTicketId,
    };

    if (this.canSendRemoteInstruction() && this.remoteBatchData) {
      data = {
        ...data,
        remoteBatchData: this.remoteBatchData,
      };
    } else {
      const { settingsStore } = this.rootStore;
      const { configuration } = settingsStore;

      if (batchTicketId) {
        this.emitDataCounter += 1;
      } else {
        this.emitDataCounter = 0;
      }

      data = {
        ...data,
        pacStatus: this.pacStatus,
        tick: this.ticks,
        inputs: this.inputs,
        outputs: this.outputs,
        channels: this.channels,
        weightsStatus: this.weightsStatus,
        meters: this.meters,
        metersStatus: this.metersStatus,
        analogInputs: this.analogInputs,
        analogOutputs: this.analogOutputs,
        isRunning: this.isRunning,
        isDemo: this.isDemo,
        isOverride: this.isOverride,
        isAbort: this.isAbort,
        isDischarge: this.isDischarge,
        discharging: this.discharging,
        isHoldDischarge: this.isHoldDischarge,
        isHoldFeed: this.isHoldFeed,
        trackedFeedPumps: this.trackedFeedPumps,
        trackedDischargeScales: this.trackedDischargeScales,
        deviceStatus: this.deviceStatus,
        waitingForDischarge: this.waitingForDischarge,
        holdFeedDevices: this.holdFeedDevices,
        finishFeedDevices: this.finishFeedDevices,
        pumpWeights: this.pumpWeights,
        scaleWeights: this.scaleWeights,
        isPacInitialCommandSent: this.isPacInitialCommandSent,
        logs: this.logs || [],
        pacVersionEmitter: this.pacVersion,
        proxyVersionEmitter: this.proxyVersion,
        batchInfoFrequency: configuration?.batchInfoFrequency,
        feedMetrics: this.feedMetrics,
        isFinishBatchPanelVisible: this.isFinishBatchPanelVisible,
        tracker: getMouseTracker(),
        emitDataCounter: this.emitDataCounter,
        gui: this.getGUIs(),
      };

      if (this.emittedDataHasNoChanges() && !this.stopObserve) {
        data = {
          ...data,
          noChanges: true,
        };
      } else {
        data = {
          ...data,
          running: this.getOptimizedBatchData(reduceSize),
          queue: reduceSize ? [] : this.queue,
          scaleQueue: reduceSize ? {} : this.scaleQueue,
        };
      }
    }

    if (FEATURE_COMPRESS_BATCH_INFO) {
      return compress(data);
    }

    return JSON.stringify(data);
  },

  getGUIs(this: IBatchStore) {
    const lastData = this.lastEmittedData ? JSON.parse(this.lastEmittedData) : null;
    const lastGUI = lastData ? lastData.gui : null;
    const currentGUI = this.devicesStore.deviceGUIs;

    if (
      (this.emitDataCounter > 0 && this.emitDataCounter <= 3) // save gui at the beginning of the load
      ||
      (lastGUI && JSON.stringify(lastGUI) !== JSON.stringify(currentGUI))
    ) {
      return currentGUI;
    }

    return null;
  },

  getOptimizedBatchData(this: IBatchStore, reduceSize = false) {
    const d: IBatchData[] = JSON.parse(JSON.stringify(this.running));
    if (d.length) {
      d.forEach((data: IBatchData) => {
        if (data.scales) {
          Object.keys(data.scales).forEach(scaleId => {
            data.scales[scaleId].forEach((s: IWeighedDataToBatch) => {
              s.feedLogs = [];
            });
          });
        }

        if (reduceSize || this.emitDataCounter > 10) {
          data.originalPayload = {};
          data.verification = {};
        }
      });
    }

    return d;
  },

  /**
   * Override data
   *
   * @param data
   */
  overrideBatchData(this: IBatchStore, data: any) {
    if (!data) {
      return;
    }

    if (data.deviceStatus) {
      let deviceStatus = new Map();
      Object.keys(data.deviceStatus).forEach(key => {
        deviceStatus.set(key.includes('-') ? key : Number(key), data.deviceStatus[key]);
      });
      this.deviceStatus = deviceStatus;
    }

    if (data.gui) {
      this.devicesStore.deviceGUIs = data.gui;
    }

    this.canEmit = false;
    this.pacStatus = data.pacStatus;
    this.pacVersionEmitter = data.pacVersionEmitter;
    this.proxyVersionEmitter = data.proxyVersionEmitter;
    this.ticks = data.ticks;
    this.inputs = data.inputs;
    this.outputs = data.outputs;
    this.channels = data.channels;
    this.weightsStatus = data.weightsStatus;
    this.meters = data.meters;
    this.metersStatus = data.metersStatus;
    this.analogInputs = data.analogInputs;
    this.analogOutputs = data.analogOutputs;
    this.isDemo = data.isDemo;
    this.isOverride = data.isOverride;
    this.isAbort = data.isAbort;
    this.isDischarge = data.isDischarge;
    this.discharging = data.discharging;
    this.isHoldDischarge = data.isHoldDischarge;
    this.isHoldFeed = data.isHoldFeed;
    this.trackedFeedPumps = data.trackedFeedPumps;
    this.trackedDischargeScales = data.trackedDischargeScales;
    this.waitingForDischarge = data.waitingForDischarge;
    this.holdFeedDevices = data.holdFeedDevices;
    this.finishFeedDevices = data.finishFeedDevices;
    this.pumpWeights = data.pumpWeights;
    this.scaleWeights = data.scaleWeights;
    this.isPacInitialCommandSent = data.isPacInitialCommandSent;
    this.displayLogs(data.logs);
    this.displayLogsNotification(data.logs);
    this.feedMetrics = data.feedMetrics ? data.feedMetrics : this.feedMetrics;
    this.isFinishBatchPanelVisible = data.isFinishBatchPanelVisible ?? data.isFinishBatchPanelVisible;

    if (this.replayMode) {
      moveReplayMouse(data.tracker);
    }

    if (data.noChanges) {
      return;
    }

    this.queue = data.queue;
    this.running = data.running;
    this.scaleQueue = data.scaleQueue;
  },

  emittedDataHasNoChanges(this: IBatchStore): boolean {
    return !!this.running.length
      && this.lastEmittedData === this.emittedDataToCompare()
      && !!this.ticks
      && this.ticks % 10 !== 0;
  },

  emittedDataToCompare(this: IBatchStore) {
    return JSON.stringify({
      running: this.running,
      gui: this.devicesStore.deviceGUIs,
    });
  },

  canControlPlant(this: IBatchStore) {
    return this.pacConnected || [Tenants.ARVADA, Tenants.DEMO, Tenants.STAGING].includes(String(this.currentTenantSlug));
  },

  emitBatchData(this: IBatchStore) {
    if (this.isDevMode) {
      return;
    }
    const { websocketStore, connectionStore, settingsStore } = this.rootStore;
    const { configuration }: any = settingsStore;
    const now = moment();
    const diff = now.diff(this.emitTime) / 1000;
    let loadStartTime = this.running.length ? this.running[0].loadStartTime : null;
    let batchTicketId = this.running.length ? this.running[0].id : null;
    const allowRemoteAccess = this.canSendRemoteInstruction();
    const frequency = loadStartTime ? 1/(configuration?.batchInfoFrequency || 5) : 1;

    // check if batching material or offline ticket
    if (isNaN(batchTicketId)) {
      loadStartTime = null;
      batchTicketId = null;
    }

    if (allowRemoteAccess && this.remoteBatchData) {
      const { type, selector } = this.remoteBatchData;
      loadStartTime = null;
      batchTicketId = null;
      logger.log('remote batch: sending ->', type, selector);
    } else if ((diff < frequency) || !connectionStore.isOnline || !websocketStore.connected || !this.canEmit
      || !websocketStore.connection || !websocketStore.isTrulyConnected || !this.canControlPlant()) {
      return;
    }

    this.emitTime = moment();

    let data = this.getBatchDataToEmit(false, batchTicketId);
    let dataSize = FEATURE_COMPRESS_BATCH_INFO ? data.length : new Blob([data]).size;
    const maxSize = 256 * 1000; // 256KB

    if (FEATURE_COMPRESS_BATCH_INFO) {
      const decompressed = decompress(data);
      logger.debug('compressed:', FEATURE_COMPRESS_BATCH_INFO, dataSize, data,
        'decompressed:', new Blob([JSON.stringify(decompressed)]).size, decompressed, 'remote:', allowRemoteAccess);
    }

    if (dataSize > maxSize) {
      logger.log(`The maximum message size send by websocket is exceeded ${dataSize} trying to resize!`);
      data = this.getBatchDataToEmit(true, batchTicketId);
      dataSize = FEATURE_COMPRESS_BATCH_INFO ? data.length : new Blob([data]).size;
    }

    this.logs = [];
    this.lastEmittedData = this.emittedDataToCompare();

    if (dataSize > maxSize) {
      const log = `The maximum message size send by websocket is exceeded ${dataSize} sending is blocked!`;
      logger.warn(log);
      this.saveLog(log);
    } else {
      logger.debug('ws:', WebsocketEvent.BATCH_INFO, this.plantId, loadStartTime, `${dataSize/1000} KB`);
      websocketStore.connection?.invoke(WebsocketEvent.BATCH_INFO, this.plantId, loadStartTime, data, {
        trace: true,
        remote: allowRemoteAccess,
        batchTicketId,
      })
        .catch((e: any) => {
          return logger.error(e);
        });
    }
  },

  /**
   * Start observe batch data when connection is back
   *
   * Extracted from an arrow class field — wrap with arrow when passing as callback
   * so that `this` is correctly bound (prototype methods are not auto-bound).
   */
  onWSClose(this: IBatchStore) {
    const { websocketStore } = this.rootStore;

    if (websocketStore.isTrulyConnected) {
      this.observeBatchData();
    } else {
      this.timers.set(timerKey.websocketClose(), () => {
        this.onWSClose();
      }, 2000, 'Websocket close handler');
    }
  },

  /**
   * Observe data from other emitter
   */
  observeBatchData(this: IBatchStore) {
    const { websocketStore, connectionStore } = this.rootStore;
    this.timers.clear(timerKey.observeBatchData());

    if (!websocketStore.connection) {

      if (connectionStore.isOnline) {
        websocketStore.connect().catch((e) => logger.warn(`ws: ${e.message}`));
      }

      this.timers.set(timerKey.observeBatchData(), () => this.observeBatchData(), 1000, 'Observe batch data');

      return;
    }

    // Use arrow wrapper to preserve `this` binding for prototype method (was arrow class field)
    websocketStore.connection.onclose(() => this.onWSClose());

    websocketStore.connection.on(WebsocketEvent.BATCH_INFO, (plantId, payload) => {
      if (plantId !== this.plantId || this.stopObserve) {
        return;
      }

      if (this.isDevMode) {
        this.canEmit = true;
        return;
      }

      try {
        const { batchInfo, remote } = payload;
        if (!batchInfo) {
          logger.warn('ws: missing batchInfo', payload);
          return;
        }

        const isCompressed = Array.isArray(batchInfo);
        let data;

        if (isCompressed) {
          data = decompress(batchInfo);
        } else {
          try {
            data = JSON.parse(batchInfo);
          } catch (e) {
            logger.warn('ws: couldn\'t parse batchInfo', e, batchInfo);
            return;
          }
        }

        if (data.host !== window.location.host) {
          logger.log('user is using different url', data.host, window.location.host);
        }

        if (this.emitter !== data.emitter && !this.replayMode) {
          // Check stored emitter to determine if this is from the same browser
          const storedEmitter = storage.getItem(EMITTER_KEY);
          const dataAge = Date.now() - Number(data.timestamp);
          const isSameUser = data.username === this.rootStore.newAuthStore.user?.name;
          const isSameBrowser = storedEmitter && data.emitter === storedEmitter;

          // if this is the same browser and user and data is older than 5sec it's likely the previous session before refresh
          if (isSameUser && isSameBrowser && dataAge > 5000) {
            logger.log(`Ignoring stale batch data from same browser session (age: ${dataAge}ms, emitter: ${data.emitter})`);
            return;
          }

          if (data.remoteBatchData) {
            if (this.canExecuteRemoteCode(data)) {
              const { username, login, emitter, userTimeZone, remoteBatchData } = data;
              const { type, selector, batchData } = remoteBatchData;
              const log = `remote batch -> ${username} -> ${type} ${selector || ''}, access: ${remote}`;

              logger.log(log, data);
              this.saveLog(log);

              if (!remote) {
                logger.warn('remote data is not trusted, no user permission', username, type, payload);
                return;
              }

              if (this.remoteUser && this.remoteUser?.emitter !== emitter) {
                logger.warn('only one remote user allowed', this.remoteUser, login);
                this.showWarning(`Only one remote user allowed! Another remote user ${username} - ${userTimeZone} is trying to take control`);
                return;
              }

              const remoteUser = (data: any) => ({
                username: data.username,
                login: data.login,
                userTimeZone: data.userTimeZone,
                emitter: data.emitter,
              });

              this.remoteUser = remoteUser(data);

              this.timers.set(timerKey.remoteUser(), () => {
                this.remoteUser = null;
              }, 1000*60, 'Clear remote user');

              switch (type) {
                case RemoteControlEventType.ABORT:
                  this.executeAbort(batchData.abortWithQueue);
                  break;
                case RemoteControlEventType.BATCH:
                  if (batchData.isDemo !== this.isDemo) {
                    this.isDemo = !!batchData.isDemo;
                  }
                  this.initData({ ...batchData, remoteUser: this.remoteUser });
                  break;
                case RemoteControlEventType.USER_INFO:
                  this.remoteUser = batchData.activeRemoteUser ? remoteUser(data) : null;
                  break;
                case RemoteControlEventType.INFO:
                  this.showInfo(batchData.info);
                  break;
                case RemoteControlEventType.CLICK:
                case RemoteControlEventType.MOUSEDOWN:
                case RemoteControlEventType.MOUSEUP:
                  executeRemoteCode(data.remoteBatchData, (error) => {
                    logger.warn(error);
                    this.saveLog(`error: ${error}`);
                  });
                  break;
                case RemoteControlEventType.FINISH_FEED_DEVICE:
                  this.finishFeedDevices.push(batchData.deviceId);
                  break;
                case RemoteControlEventType.FINISH_DISCHARGE_DEVICE:
                  addFinishDischargeDevice(this, batchData.deviceId);
                  break;
                case RemoteControlEventType.ADD_EXTRA_WATER: {
                  const { id, waterId, waterTarget } = batchData;
                  const runningData = this.running.find(v => v.id === id);
                  this.startExtraWater(runningData, waterId, waterTarget);
                  break;
                }
                case RemoteControlEventType.FINISH_BATCH: {
                  const { id } = batchData;
                  const runningData = this.running.find(v => v.id === id);
                  this.finishBatch(runningData, true);
                  break;
                }
                case RemoteControlEventType.HOLD_LOAD:
                  this.holdLoad(batchData.id, batchData.drop);
                  break;
                case RemoteControlEventType.RESUME_LOAD:
                  this.resumeLoad(batchData.id, batchData.drop);
                  break;
                case RemoteControlEventType.REMOVE_LOAD:
                  this.executeRemoveLoad(batchData.id, batchData.drop);
                  break;
                case RemoteControlEventType.REMOVE_FROM_QUEUE:
                  this.removeFromQueue(batchData.id);
                  break;
                case RemoteControlEventType.START_QUEUE:
                  this.startBatch();
                  break;
                default:
                  logger.log('unknown remote event type', type);
              }

            }
            return;
          }

          const lastComingDiff = this.batchInfoComingTime ? moment().diff(this.batchInfoComingTime) / 1000 : 0;

          if (this.batchInfoComingTime && lastComingDiff < 1) {
            logger.debug('last batchInfo below 1 sec:', lastComingDiff);
            return;
          }

          if (this.canEmit && this.running.length && !data.running?.length) {
            logger.warn(`${data.username} is trying to take over the Batch Control during the load with no data`);
            return;
          }

          this.batchInfoComingTime = moment();
          this.stop();
          const startTime = moment.tz(Number(data.timestamp), this.timezone);
          const diff = moment().tz(this.timezone).diff(startTime);
          const dataSize = new Blob([JSON.stringify(data)]).size;
          logger.log('ws:', WebsocketEvent.BATCH_INFO, `received after ${diff/1000} sec, ${dataSize/1000} kB, diff ${lastComingDiff} sec,
           compressed?: ${isCompressed}, size: ${isCompressed ? batchInfo.length/1000 : dataSize/1000} kB`);

          this.timers.clear(timerKey.finishReplay());
          this.overrideBatchData(data);
        }

      } catch (e) {
        logger.warn('Can\'t parse received data from websocket', e);
      }
    });
  },

};
